
"use client";

import type { ReactNode, Dispatch, SetStateAction } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { type User, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth, db, serverTimestamp } from '@/lib/firebase'; // Added db and serverTimestamp
import { doc, setDoc, getDoc } from 'firebase/firestore'; // Added Firestore imports
import { useRouter, usePathname } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import type { AuthModalMode } from '@/lib/types';
import { useI18n } from './i18n-context';


const ADMIN_EMAILS = ["msjithin996@gmail.com", "msnithin999@gmail.com"]; // Consider moving to .env if it changes per environment

/**
 * Saves or updates a user's profile in the 'users' collection in Firestore.
 * @param user The Firebase Auth User object.
 */
export async function updateUserInFirestore(user: User) {
    if (!db || !user) return;

    const userDocRef = doc(db, "users", user.uid);
    try {
        const userDocSnap = await getDoc(userDocRef);
        const userData: any = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || user.email?.split('@')[0] || 'Anonymous',
            photoURL: user.photoURL,
            lastSignInTime: serverTimestamp(),
        };

        if (!userDocSnap.exists()) {
            userData.createdAt = serverTimestamp();
            console.log(`Creating new user document for UID: ${user.uid}`);
        } else {
            console.log(`Updating user document for UID: ${user.uid}`);
        }

        await setDoc(userDocRef, userData, { merge: true });
    } catch (error) {
        console.error("Error saving/updating user data to Firestore:", error);
    }
}


interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  logout: () => Promise<void>;
  isAuthModalOpen: boolean;
  setAuthModalOpen: Dispatch<SetStateAction<boolean>>;
  authModalMode: AuthModalMode;
  setAuthModalMode: Dispatch<SetStateAction<AuthModalMode>>;
  redirectPath: string | null;
  openAuthModal: (mode: AuthModalMode, redirectPath?: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<AuthModalMode>('login');
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useI18n();

  useEffect(() => {
    if (!db) {
      console.warn("AuthProvider: Firestore (db) is not available. User data will not be synced to Firestore.");
    }

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      const wasUserNull = user === null;
      setUser(currentUser);
      const isAdminUser = currentUser?.email ? ADMIN_EMAILS.includes(currentUser.email) : false;
      setIsAdmin(isAdminUser);
      
      if (currentUser) {
        if (wasUserNull) {
          await updateUserInFirestore(currentUser);
        }
        
        if (isAuthModalOpen) {
          setAuthModalOpen(false);
          // On successful login, perform the redirect.
          if (redirectPath) {
            router.push(redirectPath);
            setRedirectPath(null); // Clear the path after redirecting
          }
        }
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, loading, isAuthModalOpen, redirectPath, router]); 

  const logout = async () => {
    setLoading(true);
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setIsAdmin(false);
      setAuthModalOpen(false); // Close modal on logout
      router.push('/'); 
    } catch (error) {
      console.error("Error signing out: ", error);
    } finally {
      setLoading(false);
    }
  };

  const openAuthModal = (mode: AuthModalMode, path?: string) => {
    setRedirectPath(path || pathname); // Store the current path or a specified one
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  };


  return (
    <AuthContext.Provider value={{ user, loading, isAdmin, logout, isAuthModalOpen, setAuthModalOpen, authModalMode, setAuthModalMode, redirectPath, openAuthModal }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
